import type {
  AccountConfig,
  AccountInfo,
  ApiError,
  AvailabilityResponse,
  CaptchaResponse,
  DeleteMessagesResponse,
  MailboxUsage,
  UserSettings,
  UserSettingsUpdate,
  DnsblResult,
  RemovalRequestBody,
  RemovalResponse,
  RemovalStatusResponse,
  FlagUpdate,
  Folder,
  MailboxEvent,
  MessageDetail,
  MessageListResponse,
  SessionInfo,
} from '@webmail/shared';

/** Fehler der API mit Statuscode */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: string,
    readonly params?: Record<string, number | string>,
  ) {
    super(message);
  }
}

let csrfToken = '';
let onUnauthorized: () => void = () => {};

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken;

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : null,
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as T | ApiError;
  if (!res.ok) {
    const err = data as ApiError;
    // 401 bei Login/Konto-Formularen = falsche Eingabe, nicht abgelaufene Sitzung
    if (res.status === 401 && !url.startsWith('/api/auth/login') && !url.startsWith('/api/accounts') && url !== '/api/session')
      onUnauthorized();
    throw new ApiRequestError(res.status, err.error ?? 'error', err.message ?? res.statusText, err.detail, err.params);
  }
  return data as T;
}

const q = (folder: string) => `folder=${encodeURIComponent(folder)}`;

export const api = {
  async session(): Promise<SessionInfo | null> {
    try {
      const s = await request<SessionInfo>('GET', '/api/session');
      csrfToken = s.csrfToken;
      return s;
    } catch {
      return null;
    }
  },
  async login(email: string, password: string): Promise<SessionInfo> {
    const s = await request<SessionInfo>('POST', '/api/auth/login', { email, password });
    csrfToken = s.csrfToken;
    return s;
  },
  logout: () => request<void>('POST', '/api/auth/logout'),

  // Kontoverwaltung
  accountConfig: () => request<AccountConfig>('GET', '/api/accounts/config'),
  availability: (localPart: string) =>
    request<AvailabilityResponse>('GET', `/api/accounts/availability?localPart=${encodeURIComponent(localPart)}`),
  async createAccount(localPart: string, password: string, days: number): Promise<SessionInfo> {
    const s = await request<SessionInfo>('POST', '/api/accounts', { localPart, password, days });
    csrfToken = s.csrfToken;
    return s;
  },
  async reactivate(email: string, password: string, days: number): Promise<SessionInfo> {
    const s = await request<SessionInfo>('POST', '/api/accounts/reactivate', { email, password, days });
    csrfToken = s.csrfToken;
    return s;
  },
  account: () => request<AccountInfo>('GET', '/api/account'),
  extend: (days: number) => request<AccountInfo>('POST', '/api/account/extend', { days }),
  /** Ohne IP: Prüfung der eigenen IP (ip = null, wenn der Besucher kein IPv4 nutzt) */
  dnsbl: (ip?: string) =>
    request<DnsblResult | { ip: null; zone: string; removalEnabled?: boolean }>(
      'GET',
      `/api/dnsbl${ip ? `?ip=${encodeURIComponent(ip)}` : ''}`,
    ),
  captcha: () => request<CaptchaResponse>('GET', '/api/captcha'),
  requestRemoval: (body: RemovalRequestBody) => request<RemovalResponse>('POST', '/api/dnsbl/removal', body),
  removalStatus: (trackingId: string) =>
    request<RemovalStatusResponse>('GET', `/api/dnsbl/removal/${encodeURIComponent(trackingId)}`),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('POST', '/api/account/password', { currentPassword, newPassword }),
  folders: () => request<Folder[]>('GET', '/api/folders'),
  messages: (folder: string, page = 1) =>
    request<MessageListResponse>('GET', `/api/messages?${q(folder)}&page=${page}`),
  /** images: true = externe Bilder laden; weggelassen = Einstellung des Nutzers */
  message: (folder: string, uid: number, images?: boolean) =>
    request<MessageDetail>('GET', `/api/messages/${uid}?${q(folder)}${images === undefined ? '' : `&images=${images ? 1 : 0}`}`),
  deleteMessages: (folder: string, target: number[] | 'all') =>
    request<DeleteMessagesResponse>(
      'POST',
      '/api/messages/delete',
      target === 'all' ? { folder, all: true } : { folder, uids: target },
    ),
  /** Mehrere Nachrichten als ZIP mit .eml-Dateien */
  zipUrl: (folder: string, uids: number[]) => `/api/messages/zip?${q(folder)}&uids=${uids.join(',')}`,
  usage: () => request<MailboxUsage>('GET', '/api/mailbox/usage'),
  settings: () => request<UserSettings>('GET', '/api/settings'),
  saveSettings: (s: UserSettingsUpdate) => request<UserSettings>('PUT', '/api/settings', s),
  deleteAccount: (password: string) => request<void>('POST', '/api/account/delete', { password }),
  setFlags: (folder: string, uid: number, flags: FlagUpdate) =>
    request<void>('PATCH', `/api/messages/${uid}?${q(folder)}`, flags),
  /** Original der Nachricht als .eml-Datei */
  rawUrl: (folder: string, uid: number) => `/api/messages/${uid}/raw?${q(folder)}`,
  partUrl: (folder: string, uid: number, part: string) =>
    `/api/messages/${uid}/parts/${encodeURIComponent(part)}?${q(folder)}&download=1`,

  /** Echtzeit-Ereignisse (neue Mails etc.). Gibt eine Funktion zum Beenden zurück. */
  subscribe(onEvent: (e: MailboxEvent) => void): () => void {
    const source = new EventSource('/api/events');
    source.addEventListener('mailbox', (ev) => {
      try {
        onEvent(JSON.parse((ev as MessageEvent<string>).data) as MailboxEvent);
      } catch {
        // ignorieren
      }
    });
    return () => source.close();
  },
};
